import { AxiosResponse } from 'axios';
import i18n from '@/i18n';


const MAINTENANCE_URL = '/website-api/maintenance_forms';

/**
 * Parses a CSV row into an array of values, respecting quoted fields.
 * Handles commas within quoted values correctly.
 * 
 * parseCSVRow('foo,"bar, baz",qux') // returns ['foo', '"bar, baz"', 'qux']
 */
const parseCSVRow = (row: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          inQuotes = !inQuotes;
          current += char;
        } else if (char === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current);
      return result;
    };

/**
 * Axios response middleware that translates maintenance form CSV exports.
 * When the locale is French and the response is a CSV export from the maintenance forms endpoint,
 * it handles translation for column values that have static French translations, such as the "Status".
 */
export const maintenanceTranslationMiddleware = (response: AxiosResponse): AxiosResponse => {
    const isPostRequest = response.config.method?.toUpperCase() === 'POST';
    const isMaintenanceEndpoint = response.config.url?.includes(MAINTENANCE_URL);
    const isCSV = response.config.url?.includes('csvExport=true')

    if (!isPostRequest || !isMaintenanceEndpoint || !isCSV || i18n.locale !== 'fr') {
        return response;
    }

    const statusTranslationMap: Record<string, string> = {
        requested: 'staticSelectOptions.requested',
        scheduled: 'staticSelectOptions.workScheduled',
        resolved: 'staticSelectOptions.resolved',
        declined: 'staticSelectOptions.declined',
        spam: 'staticSelectOptions.spam',
    }
    const csvString = response.data;

    if (typeof csvString !== 'string') {
        return response;
    }

    const rows = csvString.split('\n');
    const header = parseCSVRow(rows[0]);
    const statutIndex = header.findIndex((col) => col.trim() === 'Statut');

    // Return if 'Statut' column does not exist or there are no rows to process (only header or empty CSV)
    if (statutIndex === -1 || rows.length <= 1) {
        return response;
    }

    const translatedRows = rows.map((row, index) => {
        // Keep header as-is since in the response, it's already translated
        if (index === 0) return row;

        const columns = parseCSVRow(row);
        if (columns[statutIndex]) {
        const statusValue = columns[statutIndex].trim();
        if (statusTranslationMap[statusValue]) {
            columns[statutIndex] = `${i18n.t(statusTranslationMap[statusValue])}`;
        }
        }
        return columns.join(',');
    });

    response.data = translatedRows.join('\n');

  return response;
};
